import React, { useState, useEffect } from 'react';
import {
    Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    Table, TableBody, TableCell, TableHead, TableRow, Typography,
    Paper, Stack, Grid, CircularProgress, Card, IconButton, InputAdornment,
    Select, MenuItem
} from '@mui/material';  // ✅ added Select & MenuItem
import { collection, query, where, getDocs, addDoc, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { toast } from 'react-toastify';
import TablePagination from '@mui/material/TablePagination';
import { useParams } from "react-router-dom";
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import Shopheader from '../Shop/Shopheader';
import Mashalheader from '../Mashal/Mashalheader'
import Wholesalerheader from '../Wholesale/Wholesaleheader';


const Products = () => {
    const { category } = useParams();

    const [open, setOpen] = useState(false);
    const [formData, setFormData] = useState({
        productname: '',
        company: '',
        purchaserate: '',
        salerate: '',
        size: '',
        hsncode: '',
        gst: 0,
        productdate: new Date().toISOString().split("T")[0]
    });

    const [sizes, setSizes] = useState([]);  // ✅ store product sizes
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState(null);

    useEffect(() => {
        fetchProducts();
        fetchSizes(); // ✅ fetch sizes when page loads
    }, [category]);

    // Fetch sizes
    const fetchSizes = async () => {
        try {
            const q = query(
                collection(db, 'productsizes')
            );
            const snapshot = await getDocs(q);
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setSizes(data);
        } catch (err) {
            console.error("Error fetching sizes:", err);
        }
    };


    const fetchProducts = async () => {
        try {
            const q = query(
                collection(db, 'products'),
                where('category', '==', category)
            );
            const snapshot = await getDocs(q);
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setProducts(data);
        } catch (error) {
            console.error("Error fetching products:", error);
        }
    };

    const handleOpen = () => {
        setFormData({
            productname: '',
            company: '',
            purchaserate: '',
            salerate: '',
            size: '',
            hsncode: '',
            gst: 0,
            productdate: new Date().toISOString().split("T")[0]
        });
        setErrors({});
        setEditMode(false);
        setEditId(null);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setLoading(false);
        setErrors({});
    };

    const handleChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    // ✅ Validation
    const validate = () => {
        let temp = {};
        temp.productname = formData.productname ? "" : "Product name is required";
        temp.salerate = formData.salerate ? "" : "विक्री रेट  is required";
        temp.size = formData.size ? "" : "Size is required";

        setErrors(temp);
        return Object.values(temp).every(x => x === "");
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setLoading(true);

        try {
            const { productname, company, purchaserate, salerate, size, hsncode, gst, productdate } = formData;
            const propid = auth.currentUser?.uid;

            if (editMode && editId) {
                await updateDoc(doc(db, 'products', editId), {
                    category, productname, company, purchaserate, salerate, size, hsncode, gst, productdate
                });
                toast.success("प्रोडक्ट updated");
            } else {
                await addDoc(collection(db, 'products'), {
                    category, productname, company, purchaserate, salerate, size, hsncode, gst, productdate, propid
                });
                toast.success("प्रोडक्ट added");
            }

            fetchProducts();
            handleClose();
        } catch (err) {
            console.error(err);
            toast.error("Error: " + err.message);
        }

        setLoading(false);
    };

    const handleEdit = (prod) => {
        setFormData({
            productname: prod.productname,
            company: prod.company,
            purchaserate: prod.purchaserate,
            salerate: prod.salerate,
            size: prod.size,
            hsncode: prod.hsncode,
            gst: prod.gst,
            productdate: prod.productdate
        });
        setEditId(prod.id);
        setEditMode(true);
        setOpen(true);
    };

    const handleDelete = async (id) => {
        try {
            await deleteDoc(doc(db, 'products', id));
            toast.success("Product deleted");
            fetchProducts();
        } catch (err) {
            console.error(err);
            toast.error("Error deleting product");
        }
    };

    // ✅ Search filter
    const filtered = products.filter(prod =>
        prod.productname.toLowerCase().includes(search.toLowerCase()) ||
        prod.company.toLowerCase().includes(search.toLowerCase()) ||
        prod.hsncode.toLowerCase().includes(search.toLowerCase())
    );

    const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Box >
            {category === "shop" && <Shopheader />}
            {category === "mashal" && <Mashalheader />}
            {category === "wholesaler" && <Wholesalerheader />}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Button variant="contained" onClick={handleOpen}>Add प्रोडक्ट</Button>
                <Typography variant="h5" fontWeight="bold">प्रोडक्ट Management</Typography>
            </Stack>

            {/* ✅ Search Box */}
            <TextField
                placeholder="Search by product, company, or HSN"
                variant="outlined"
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon />
                        </InputAdornment>
                    )
                }}
                sx={{ mb: 2 }}
            />


            <Paper sx={{ overflowX: "auto" }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                            <TableCell>#</TableCell>
                            <TableCell>Actions</TableCell>
                            <TableCell>प्रोडक्ट</TableCell>
                            <TableCell>विक्री रेट </TableCell>
                            <TableCell>साईज</TableCell>
                            <TableCell>कंपनी</TableCell>
                            <TableCell>खरेदी रेट </TableCell>
                            <TableCell>HSN कोड </TableCell>
                            <TableCell>GST %</TableCell>
                            <TableCell>दिनांक </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginated.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={10} align="center">No products found</TableCell>
                            </TableRow>
                        ) : (
                            paginated.map((prod, index) => (
                                <TableRow key={prod.id}>
                                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1}>
                                            <IconButton color="primary" onClick={() => handleEdit(prod)}>
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton color="error" onClick={() => handleDelete(prod.id)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>{prod.productname}</TableCell>
                                    <TableCell>{prod.salerate}</TableCell>
                                    <TableCell>{prod.size}</TableCell>
                                    <TableCell>{prod.company}</TableCell>
                                    <TableCell>{prod.purchaserate}</TableCell>
                                    <TableCell>{prod.hsncode}</TableCell>
                                    <TableCell>{prod.gst}</TableCell>
                                    <TableCell>{prod.productdate}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>

                <TablePagination
                    rowsPerPageOptions={[10, 20, 30]}
                    component="div"
                    count={filtered.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </Paper>
            {/* Add/Edit Dialog */}
            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth

            >
                <DialogTitle>{editMode ? 'Edit' : 'Add'} प्रोडक्ट</DialogTitle>
                <DialogContent dividers>
                    <div className='row'>
                        <Grid className="col-sm-6 mt-3">
                            <TextField
                                label="प्रोडक्ट Name"
                                name="productname"
                                value={formData.productname}
                                onChange={handleChange}
                                fullWidth size="small"
                                required
                                error={!!errors.productname}
                                helperText={errors.productname}
                            />
                        </Grid>
                        <Grid className="col-sm-6 mt-3">
                            <TextField
                                label="कंपनी "
                                name="company"
                                value={formData.company}
                                onChange={handleChange}
                                fullWidth size="small"
                                required
                                error={!!errors.company}
                                helperText={errors.company}
                            />
                        </Grid>
                        <Grid className="col-sm-6 mt-3">
                            <TextField
                                label="खरेदी रेट "
                                name="purchaserate"
                                value={formData.purchaserate}
                                onChange={handleChange}
                                fullWidth size="small"
                                required
                                error={!!errors.purchaserate}
                                helperText={errors.purchaserate}
                            />
                        </Grid>
                        <Grid className="col-sm-6 mt-3">
                            <TextField
                                label="विक्री रेट "
                                name="salerate"
                                value={formData.salerate}
                                onChange={handleChange}
                                fullWidth size="small"
                                required
                                error={!!errors.salerate}
                                helperText={errors.salerate}
                            />
                        </Grid>
                        <Grid className="col-sm-6 mt-3">
                            <Select
                                name="size"   // ✅ बदललं
                                value={formData.size}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                displayEmpty
                                error={!!errors.size}
                            >
                                <MenuItem value="" disabled>Select साईज</MenuItem>
                                {sizes.map((s) => (
                                    <MenuItem key={s.id} value={s.size}>
                                        {s.size}
                                    </MenuItem>
                                ))}
                            </Select>
                            {errors.size && (
                                <Typography color="error" variant="caption">{errors.size}</Typography>
                            )}
                        </Grid>
                        <Grid className="col-sm-6 mt-3">
                            <TextField
                                label="HSN कोड "
                                name="hsncode"
                                value={formData.hsncode}
                                onChange={handleChange}
                                fullWidth size="small"
                                required
                                error={!!errors.hsncode}
                                helperText={errors.hsncode}
                            />
                        </Grid>
                        <Grid className="col-sm-6 mt-3">
                            <TextField
                                label="GST %"
                                name="gst"
                                type="number"
                                value={formData.gst}
                                onChange={handleChange}
                                fullWidth size="small"
                            />
                        </Grid>
                        <Grid className="col-sm-6 mt-3">
                            <TextField
                                label="प्रोडक्ट दिनांक "
                                name="productdate"
                                type="date"
                                value={formData.productdate}
                                onChange={handleChange}
                                fullWidth size="small"
                            />
                        </Grid>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="error">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={loading}
                        sx={{ ml: 2 }} // small spacing between buttons
                    >
                        {loading ? <CircularProgress size={24} /> : (editMode ? "Update" : "Add")}
                    </Button>
                </DialogActions>

            </Dialog>
        </Box >
    );
};

export default Products;
